// Closing-facing READ-ONLY escalation detail (R5 review-3 finding 5): the
// processor's escalation note is addressed to Closing, but the Reconcile
// sheet is processor-only and CostToCureSheet is cost_to_cure-only — this
// small sheet is the missing destination. Rendered INLINE (no portal — the
// Radix modal lock makes body portals inert).
import { useEffect } from 'react';
import { Icon } from '../atoms';
import { fmtMoney, fmtStamp } from '../format';
import { activeFiledInvoices, type DocOrderFee } from '../core';

interface EscalationSheetProps {
  fee: DocOrderFee;
  onClose: () => void;
}

export function EscalationSheet({ fee, onClose }: EscalationSheetProps) {
  const esc = fee.acks?.escalation;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!esc) {
    return null;
  }
  // Codex R5-a finding 5: a kind-less legacy record could be either subtype —
  // label it neutrally, never guess.
  const kindLabel = esc.kind === 'cost_to_cure' ? 'Cost to Cure' : esc.kind === 'missing_invoice' ? 'Missing invoice' : 'Legacy escalation';
  const hasEvidence = activeFiledInvoices(fee).length > 0;
  return (
    <div className="recon-overlay" data-doc-order>
      <div className="recon-scrim" onClick={onClose} />
      <aside className="recon-sheet" role="dialog" aria-modal="true" aria-label={`Escalation — ${fee.name}`} tabIndex={-1}>
        <header className="recon-hd">
          <div>
            <div className="recon-eyebrow">
              <Icon name="flag" size={12} />
              Escalated by Processing
            </div>
            <div className="recon-title">
              {fee.name}
              {fee.payee ? <span className="recon-payee"> · {fee.payee}</span> : null}
            </div>
          </div>
          <button className="recon-close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={15} />
          </button>
        </header>
        <div className="recon-ack">
          <div className="recon-ack-stamp">
            <Icon name="flag" size={13} />
            {kindLabel} — escalated by <b>{esc.by}</b> · {fmtStamp(esc.at)}
          </div>
          <div className="recon-ack-note">&ldquo;{esc.note}&rdquo;</div>
        </div>
        <div className="recon-stamp">
          Fee {fmtMoney(Number(fee.amount) || 0, true)} ·{' '}
          {esc.kind === 'missing_invoice'
            ? hasEvidence
              ? 'An invoice has since been filed — re-open the order review to reconcile.'
              : 'No invoice in the eFolder — resolved by escalation; the evidence requirement remains outstanding.'
            : esc.kind === 'cost_to_cure'
              ? 'Reviewed and cured at closing — see the Cost to Cure flag for the figures.'
              : 'Recorded before typed escalations — see the note above; this record does not waive the invoice requirement.'}
        </div>
      </aside>
    </div>
  );
}
